import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $countryProvincesStore, $countriesStore, $currenciesStore, $recipientByIdStore, $recipientUpdateStore } from '@lib/store';
import { useDebounce } from '@shared/hooks';
import { IbanUtils, ValidateUtils } from '@shared/utils';
import {
  PaymentApi,
  type RecipientResponse,
  type RecipientUpdateRequest,
  TransferRecipientAccountTypeEnum,
  TransferRecipientTypeEnum,
} from '@shared/api';
import { BaseInput, Button, ErrorMessage, Modal, Select } from '@shared/widgets';

const OPTIONS_TYPE = Object.entries(TransferRecipientTypeEnum).map((x) => ({ label: x[1], value: x[1] }));
const OPTIONS_ACCOUNT_TYPE = Object.entries(TransferRecipientAccountTypeEnum).map((x) => ({ label: x[1], value: x[1] }));

interface IForm {
  type: TransferRecipientTypeEnum;
  firstName: string;
  lastName: string;
  idNumber: string;
  companyName: string;
  accountNumber: string;
  accountType: TransferRecipientAccountTypeEnum;
  iban: string;
  bankName: string;
  bankProvince: string;
  bankAddress: string;
  bankCity: string;
  branchCode: string;
  bankCountryCode?: string;
  bicOrSwiftCode: string;
  countryCode: string;
  currency: string;
  email: string;
  address: string;
  postCode: string;
  city: string;
  province: string;
}

interface EditRecipientModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: RecipientResponse;
}

export const EditRecipientModal = (props: EditRecipientModalProps) => {
  const countryProvincesStore = useUnit($countryProvincesStore.store);
  const countryStore = useUnit($countriesStore.store);
  const currenciesStore = useUnit($currenciesStore.store);

  const [ibanError, setIbanError] = useState<string>('');

  const { control, formState, handleSubmit, watch, getValues, setValue, setError, clearErrors, reset } = useForm<IForm>({
    defaultValues: {
      type: props.data.type || '',
      firstName: props.data.firstName || '',
      lastName: props.data.lastName || '',
      idNumber: props.data.idNumber || '',
      companyName: props.data.companyName || '',
      accountNumber: props.data.accountNumber || '',
      accountType: props.data.accountType || '',
      iban: props.data.iban || '',
      bankName: props.data.bankName || '',
      bankProvince: props.data.bankProvince || '',
      bankAddress: props.data.bankAddress || '',
      bankCity: props.data.bankCity || '',
      branchCode: props.data.branchCode || '',
      bankCountryCode: props.data.bankCountryCode || '',
      bicOrSwiftCode: props.data.bic || '',
      countryCode: props.data.countryCode || '',
      currency: props.data.currency || '',
      email: props.data.email || '',
      address: props.data.address || '',
      postCode: props.data.postCode || '',
      city: props.data.city || '',
      province: props.data.province || '',
    },
  });
  const type = watch('type');
  const accountNumber = watch('accountNumber');
  const iban = watch('iban');
  const countryCode = watch('countryCode');

  const { t } = useTranslation('recipient');

  useEffect(() => {
    Promise.allSettled([
      $countryProvincesStore.events.getCountryProvinces(),
      $countriesStore.events.getCountries(),
      $currenciesStore.events.getCurrencies(),
    ]);
  }, []);

  const debouncedIban = useDebounce(iban);
  useEffect(() => {
    if (!props.isOpen || !debouncedIban || debouncedIban.includes(' ')) return;
    (async () => {
      setValue('bankCountryCode', undefined);
      setValue('bicOrSwiftCode', '');
      setValue('bankName', '');
      setIbanError('');

      const ibanValidation = (await PaymentApi.validateIban(debouncedIban)).data!;

      if (ibanValidation.isValid) {
        const countryCode = IbanUtils.getCountry(debouncedIban);
        const selectedCountry = countryStore.data.find((e) => e.twoLetterCode === countryCode);

        if (selectedCountry?.twoLetterCode !== getValues('bankCountryCode'))
          setValue('bankCountryCode', selectedCountry?.twoLetterCode, { shouldValidate: true });
        if (ibanValidation.bic !== undefined)
          setValue('bicOrSwiftCode', `${ibanValidation.bic}${ibanValidation.branchBic || ''}`, { shouldValidate: true });
        if (ibanValidation.bankName) setValue('bankName', ibanValidation.bankName, { shouldValidate: true });

        clearErrors('iban');
      } else {
        setError('iban', { message: t('errors.form.iban.invalidFormat', { ns: 'common' }) });
        setIbanError(t('errors.form.iban.invalidFormat', { ns: 'common' }));
      }
    })();
  }, [debouncedIban]);

  const closeHandler = () => {
    props.onClose();
    reset({
      type: props.data.type || '',
      firstName: props.data.firstName || '',
      lastName: props.data.lastName || '',
      idNumber: props.data.idNumber || '',
      companyName: props.data.companyName || '',
      accountNumber: props.data.accountNumber || '',
      accountType: props.data.accountType || '',
      iban: props.data.iban || '',
      bankName: props.data.bankName || '',
      bankProvince: props.data.bankProvince || '',
      bankAddress: props.data.bankAddress || '',
      bankCity: props.data.bankCity || '',
      branchCode: props.data.branchCode || '',
      bankCountryCode: props.data.bankCountryCode || '',
      bicOrSwiftCode: props.data.bic || '',
      countryCode: props.data.countryCode || '',
      currency: props.data.currency || '',
      email: props.data.email || '',
      address: props.data.address || '',
      postCode: props.data.postCode || '',
      city: props.data.city || '',
      province: props.data.province || '',
    });
  };
  const submitHandler = async (data: IForm) => {
    const { firstName, lastName, idNumber, companyName, ...rest } = data;

    let payload: RecipientUpdateRequest = {};
    if (data.type === TransferRecipientTypeEnum.Individual) payload = { ...rest, firstName, lastName, idNumber };
    if (data.type === TransferRecipientTypeEnum.Business) payload = { ...rest, companyName };

    await $recipientUpdateStore.events.updateRecipient({
      ...payload,
      id: props.data.id,
      bankProvince: data.bankProvince || null,
      bankAddress: data.bankAddress || null,
      bankCity: data.bankCity || null,
      branchCode: data.branchCode || null,
      bankCountryCode: data.bankCountryCode || null,
      address: data.address || null,
      postCode: data.postCode || null,
      city: data.city || null,
      province: data.province || null,
    });
    props.onClose();
    $recipientByIdStore.events.getRecipient({ id: props.data.id });
  };

  const countryProvinces = countryCode in countryProvincesStore.data ? countryProvincesStore.data[countryCode] : [];
  const isIndividual = type === TransferRecipientTypeEnum.Individual;
  const isBusiness = type === TransferRecipientTypeEnum.Business;
  return (
    <Modal isOpen={props.isOpen} onClose={closeHandler} title={t('details.modals.editRecipient.title')}>
      <form className='flex flex-col gap-4' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='type'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Select
                label={t('details.fields.type')}
                nullValue={{ isEnable: false, text: t('details.fields.type') }}
                items={OPTIONS_TYPE.map((option) => ({ ...option, label: t(`recipientType.${option.label.toLowerCase()}`, { ns: 'common' }) }))}
                required
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        {isIndividual && (
          <Controller
            name='firstName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} label={t('details.fields.name')} placeholder={t('details.fields.name')} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {isIndividual && (
          <Controller
            name='lastName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} label={t('details.fields.lastName')} placeholder={t('details.fields.lastName')} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {isIndividual && (
          <Controller
            name='idNumber'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} label={t('details.fields.idNumber')} placeholder={t('details.fields.idNumber')} />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        {isBusiness && (
          <Controller
            name='companyName'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <BaseInput {...field} label={t('details.fields.companyName')} placeholder={t('details.fields.companyName')} required />
                <ErrorMessage text={error?.message} />
              </div>
            )}
          />
        )}
        <Controller
          name='accountNumber'
          control={control}
          rules={{
            required: !iban ? t('errors.form.required', { ns: 'common' }) : false,
            pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.accountNumber')} placeholder={t('details.fields.accountNumber')} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='accountType'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Select
                label={t('details.fields.accountType')}
                nullValue={{ isEnable: false, text: t('details.fields.accountType') }}
                items={OPTIONS_ACCOUNT_TYPE.map((option) => ({
                  ...option,
                  label: t(`transferRecipientAccountType.${option.label.toLowerCase()}`, { ns: 'common' }),
                }))}
                required
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='iban'
          control={control}
          rules={{
            required: !accountNumber ? t('errors.form.required', { ns: 'common' }) : false,
            pattern: { value: ValidateUtils.noSpacesRegex, message: t('errors.form.noSpaces', { ns: 'common' }) },
            validate: () => {
              if (ibanError) return ibanError;
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput
                {...field}
                label={t('details.fields.ibanNumber')}
                placeholder={t('details.fields.ibanNumber')}
                required={!accountNumber ? true : false}
                noSpaces
                onPaste={(e) => {
                  e.preventDefault();
                  field.onChange(e.clipboardData.getData('text').replace(/\s+/g, '').trim());
                }}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankName'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.bankName')} placeholder={t('details.fields.bankName')} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankProvince'
          control={control}
          rules={{ pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Select
                label={t('details.fields.bankProvince')}
                items={countryProvinces.map((countryProvince) => ({ label: countryProvince, value: countryProvince }))}
                nullValue={{ isEnable: false, text: t('details.fields.bankProvince') }}
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankAddress'
          control={control}
          rules={{ pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.bankAddress')} placeholder={t('details.fields.bankAddress')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankCity'
          control={control}
          rules={{ pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.bankCity')} placeholder={t('details.fields.bankCity')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='branchCode'
          control={control}
          rules={{ pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.branchCode')} placeholder={t('details.fields.branchCode')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bankCountryCode'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Select
                enableSearch
                label={t('details.fields.bankCountry')}
                items={countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode }))}
                nullValue={{ isEnable: false, text: t('details.fields.bankCountry') }}
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='bicOrSwiftCode'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) },
            validate: (value) => {
              if (!ValidateUtils.validateBicCodeField(value)) return t('errors.form.bicSwift.invalidFormat', { ns: 'common' });
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.bicSwiftCode')} placeholder={t('details.fields.bicSwiftCode')} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='countryCode'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Select
                enableSearch
                label={t(
                  `details.fields.${props.data.type === TransferRecipientTypeEnum.Individual ? 'beneficiaryResidentialCountry' : 'incorporationCountry'}`,
                )}
                items={countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode }))}
                nullValue={{
                  isEnable: false,
                  text: t(
                    `details.fields.${props.data.type === TransferRecipientTypeEnum.Individual ? 'beneficiaryResidentialCountry' : 'incorporationCountry'}`,
                  ),
                }}
                required
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='currency'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <Select
                label={t('details.fields.currency')}
                items={currenciesStore.data.map((currency) => ({ label: currency, value: currency }))}
                nullValue={{ isEnable: false, text: t('details.fields.currency') }}
                required
                value={field.value}
                onChange={field.onChange}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='email'
          control={control}
          rules={{ pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.email')} placeholder={t('details.fields.email')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='address'
          control={control}
          rules={{ pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.address')} placeholder={t('details.fields.address')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='postCode'
          control={control}
          rules={{ pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.postCode')} placeholder={t('details.fields.postCode')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='city'
          control={control}
          rules={{ pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.townCity')} placeholder={t('details.fields.townCity')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='province'
          control={control}
          rules={{ pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.incorrectValue', { ns: 'common' }) } }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} label={t('details.fields.provinceRegion')} placeholder={t('details.fields.provinceRegion')} />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('buttons.save', { ns: 'common' })}
        </Button>
      </form>
    </Modal>
  );
};
